import { Box, Button, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CreateDestinationFormValues } from '../../organisms/create-destination/create-destination-form';

import { InputField } from '@/components/atoms/input-field';
import StepTitle from '@/components/atoms/step-title';
import {
  CreateDestinationContext,
  CreateDestinationEvent,
} from '@/features/destinations/utils/machine/create-destination-machine';

interface Props {
  context: CreateDestinationContext;
  send: (event: CreateDestinationEvent) => void;
}

export const DestinationInfo = ({ context, send }: Props) => {
  const { t } = useTranslation(['createDestination', 'common']);

  const {
    control,
    formState: { isValid },
  } = useFormContext<CreateDestinationFormValues>();

  return (
    <div>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridAutoFlow: 'row',
        }}>
        <StepTitle
          title={t('common:stepTitle', {
            step: context.step,
            title: t(context.content),
          })}
        />
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <InputField
              {...field}
              label={t('createDestination:form.name')}
              error={!!error?.message}
              fullWidth
              helperText={error?.message}
              required
            />
          )}
        />
        <Typography variant="caption">{t('createDestination:form.nameTip')}</Typography>
        <Controller
          control={control}
          name="floor"
          render={({ field, fieldState: { error } }) => (
            <InputField
              {...field}
              label={t('createDestination:form.floor')}
              error={!!error?.message}
              fullWidth
              helperText={error?.message}
              required
            />
          )}
        />
        <Typography variant="caption">{t('createDestination:form.floorTip')}</Typography>
        <Controller
          control={control}
          name="description"
          render={({ field, fieldState: { error } }) => (
            <InputField
              {...field}
              label={t('createDestination:form.description')}
              error={!!error?.message}
              fullWidth
              helperText={error?.message}
              required
            />
          )}
        />
        <Controller
          control={control}
          name="maxCapacity"
          render={({ field, fieldState: { error } }) => (
            <InputField
              {...field}
              label={t('createDestination:form.maxCapacity')}
              type="number"
              error={!!error?.message}
              fullWidth
              helperText={error?.message}
              required
            />
          )}
        />
        <Typography variant="caption">{t('createDestination:form.maxCapacityTip')}</Typography>
        <Controller
          control={control}
          name="accessBeforeVisitInMinutes"
          render={({ field, fieldState: { error } }) => (
            <InputField
              {...field}
              label={t('createDestination:form.accessBefore')}
              type="number"
              error={!!error?.message}
              fullWidth
              helperText={error?.message}
              required
            />
          )}
        />
        <Typography variant="caption">{t('createDestination:form.accessBeforeTip')}</Typography>
        <Controller
          control={control}
          name="accessAfterVisitInMinutes"
          render={({ field, fieldState: { error } }) => (
            <InputField
              {...field}
              label={t('createDestination:form.accessAfter')}
              type="number"
              error={!!error?.message}
              fullWidth
              helperText={error?.message}
              required
            />
          )}
        />
        <Typography variant="caption">{t('createDestination:form.accessAfterTip')}</Typography>
        <div>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 3,
            }}>
            <Button
              color="primary"
              size="large"
              type="button"
              variant="contained"
              disabled={!isValid}
              onClick={() => {
                send({ type: 'NEXT' });
              }}>
              {t('common:button.nextStep')}
            </Button>
          </Box>
        </div>
      </Box>
    </div>
  );
};
